import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { db } from "../../lib/firebase";
import { doc, getDoc, updateDoc, deleteField } from "firebase/firestore";
import { sendBuyNotificationEmail } from "../../lib/buyEmail";
import "../../styles/Deposit.css";

const BuyCrypto = ({ loading, handleCloseModal, user }) => {
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Check if user already has a pending payment
  useEffect(() => {
    const checkPendingPayment = async () => {
      if (user?.uid) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.pendingPayment) {
            setPendingPayment(data.pendingPayment);
          }
        }
      }
    };
    checkPendingPayment();
  }, [user]);

  // Fetch buy account details from Firestore (users/buy)
  useEffect(() => {
    async function fetchAccount() {
      try {
        const snap = await getDoc(doc(db, "users", "buy"));
        if (snap.exists()) {
          setAccount(snap.data());
        }
      } catch (err) {
        console.error("Error loading buy account:", err);
      }
    }
    fetchAccount();
  }, []);

  // Save payment and send email notification
  const handleSavePayment = async () => {
    if (!user?.uid || submitting) return;
    if (!amount || Number(amount) <= 0) return;
    setSubmitting(true);
    try {
      const paymentData = {
        amount: Number(amount),
        accountType: account?.accountType || "",
        accountName: account?.name || "",
        address: account?.address || "",
        memo: account?.memo || "",
        date: new Date().toISOString(),
        status: "pending",
        type: "buy",
      };

      await updateDoc(doc(db, "users", user.uid), {
        pendingPayment: paymentData,
      });

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      await sendBuyNotificationEmail(userData, paymentData);

      setPendingPayment(paymentData);
    } catch (err) {
      console.error("Error saving payment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel pending payment
  const handleCancelPayment = async () => {
    if (!user?.uid) return;
    setCanceling(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        pendingPayment: deleteField(),
      });
      setPendingPayment(null);
    } catch (err) {
      console.error("Error canceling payment:", err);
    } finally {
      setCanceling(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    });
  };

  if (loading || !account) {
    return (
      <div className="deposit-page">
        <h2>Buy Crypto</h2>
        <p>Loading payment details...</p>
      </div>
    );
  }

  // If there's a pending payment, show confirmation screen
  if (pendingPayment) {
    const isDeposit = pendingPayment.type === "deposit";
    return (
      <div className="deposit-page">
        <div className="pending-confirmation">
          <h2>Waiting for {isDeposit ? "Deposit" : "Buy"} Confirmation</h2>
          <p className="confirm-text">
            Your payment is being confirmed. Please wait while we verify your
            transaction.
          </p>
          <div className="pending-details">
            <div className="pending-row">
              <span>Transaction Type</span>
              <span style={{ textTransform: "capitalize" }}>{pendingPayment.type || "buy"}</span>
            </div>
            <div className="pending-row">
              <span>Amount</span>
              <span>${pendingPayment.amount.toLocaleString()}</span>
            </div>
            {pendingPayment.accountType && (
              <div className="pending-row">
                <span>{isDeposit ? "Account Type" : "Payment Method"}</span>
                <span>{pendingPayment.accountType}</span>
              </div>
            )}
            <div className="pending-row">
              <span>Status</span>
              <span className="pending-status">{pendingPayment.status}</span>
            </div>
            <div className="pending-row">
              <span>Date</span>
              <span>{new Date(pendingPayment.date).toLocaleDateString()}</span>
            </div>
          </div>
          <button
            className="deposit-btn secondary"
            style={{ marginTop: "20px", width: "100%" }}
            onClick={handleCancelPayment}
            disabled={canceling}
          >
            {canceling ? "Deleting..." : isDeposit ? "Cancel Deposit" : "Cancel Payment"}
          </button>
        </div>
      </div>
    );
  }

  // Main view: show account details + amount input + "I've paid"
  return (
    <div className="deposit-page">
      {copied && <div className="copied-notification">Copied!</div>}

      <h2>Buy Crypto</h2>
      <p className="box-text">
        Send your payment to the account below, enter the amount, and
        click <strong>"I've paid"</strong>.
      </p>

      {/* Payment account details */}
      <div className="pending-details" style={{ marginBottom: "20px" }}>
        <div className="pending-row">
          <span>Account Type</span>
          <span>{account.accountType}</span>
        </div>
        <div className="pending-row">
          <span>Name on Account</span>
          <span>{account.name}</span>
        </div>
        <div className="pending-row">
          <span>Address</span>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                wordBreak: "break-all",
                fontFamily: "monospace",
                fontSize: "13px",
              }}
            >
              {account.address}
            </span>
            <button
              className="copy-button"
              onClick={() => handleCopy(account.address)}
              title="Copy"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </span>
        </div>
        {account.memo && (
          <div className="pending-row">
            <span>Memo</span>
            <span>{account.memo}</span>
          </div>
        )}
      </div>

      {/* Amount input + I've paid button */}
      <label>
        Amount (USD):
        <input
          className="input-field"
          type="number"
          placeholder="eg. 1000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0"
          step="any"
          required
        />
      </label>

      <button
        className="delete-button"
        onClick={handleSavePayment}
        disabled={submitting || !amount || Number(amount) <= 0}
      >
        {submitting ? "Submitting..." : "I've paid!"}
      </button>
    </div>
  );
};

export default BuyCrypto;
